/**
 * Triage Queue Page
 *
 * Displays items needing triage with batch processing capabilities.
 * Part of Workstream 3: Triage System
 */

import { useState, useMemo, useEffect } from 'react';
import { InfoModal, type InfoSection } from '../../../components/prioritization/InfoModal';
import {
  Check,
  X,
  ArrowRight,
  HelpCircle,
  Clock,
  Search,
  ChevronDown,
  ChevronUp,
  Inbox,
  CheckCircle,
  Lock,
  LayoutList,
  Table2,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { ItemListView } from '../../../components/prioritization/item-list/ItemListView';
import { shallow } from 'zustand/shallow';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import {
  itemIsAwaitingTriage,
  triageFinalizedOutOfQueueIds,
} from '../../../lib/intakeQueueVisibility';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useTriageStore } from '../../../stores/prioritization/triageStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import type { Item } from '../../../types/prioritization/lifecycle';
import type { PermissionAction } from '../../../types/prioritization/permissions';
import type { TriageOutcome } from '../../../types/prioritization/triage';
import { createEmptyQuickChecks } from '../../../types/prioritization/triage';
import { TriageRelationshipsPanel } from '../../../components/prioritization/TriageRelationshipsPanel';

interface QueueItem {
  id: string;
  title: string;
  description?: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: Date;
  source?: string;
  daysInQueue: number;
}

/**
 * Same queue membership as the Triage sidebar badge (useAttentionCounts): items in the
 * workspace-scoped items store that pass `itemIsAwaitingTriage`, not a second Supabase
 * query that can disagree when workspace hints / filters diverge.
 */
function useRealQueueItems(): { items: QueueItem[]; loading: boolean } {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const storeItems = useItemsStore((s) => s.items);
  const itemsLoading = useItemsStore((s) => s.syncState.isLoading);
  const assessments = useTriageStore((s) => s.assessments, shallow);
  const currentUserId = useUserStore((s) => s.userId);
  const currentUserName = useUserStore((s) => s.userName);

  const awaitingItems = useMemo(() => {
    const triagedOut = triageFinalizedOutOfQueueIds(assessments);
    return storeItems.filter((item) => itemIsAwaitingTriage(item, triagedOut));
  }, [storeItems, assessments]);

  const sortedAwaiting = useMemo(
    () =>
      [...awaitingItems].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [awaitingItems]
  );

  useEffect(() => {
    let cancelled = false;
    const now = new Date();

    const toQueueRows = (
      items: Item[],
      userNameMap: Record<string, string>
    ): QueueItem[] =>
      items.map((c) => {
        const row = c as Item & {
          created_by_user_id?: string;
          createdByUserId?: string;
          submitted_at?: string;
          submittedBy?: string;
        };
        const createdByUserId = row.created_by_user_id ?? row.createdByUserId;
        const submitterKey = createdByUserId || row.createdBy;
        const metadataName =
          c.metadata?.submitterName || c.metadata?.createdByName;
        const submittedByName = submitterKey
          ? userNameMap[submitterKey] || metadataName || 'User'
          : 'Anonymous';
        const created = new Date(c.createdAt);

        return {
          id: c.id,
          title: c.title || 'Untitled',
          description: c.description,
          submittedBy: submitterKey,
          submittedByName,
          submittedAt: row.submitted_at ? new Date(row.submitted_at) : created,
          source: row.source || c.source || 'Direct',
          daysInQueue: Math.floor(
            (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
          ),
        };
      });

    // Paint immediately so the list matches the sidebar count while user names resolve.
    setQueueItems(toQueueRows(sortedAwaiting, {}));

    async function resolveQueueRows() {
      const supabase = getSafeSupabaseClient();

      const creatorIds = [
        ...new Set(
          sortedAwaiting
            .map((c) => {
              const r = c as Item & {
                created_by_user_id?: string;
                createdByUserId?: string;
              };
              return r.created_by_user_id ?? r.createdByUserId ?? c.createdBy;
            })
            .filter(Boolean) as string[]
        ),
      ];
      const looksLikeEmail = (s: string) => typeof s === 'string' && s.includes('@');
      const uuidLike = creatorIds.filter((id) => !looksLikeEmail(id));
      const emailLike = creatorIds.filter((id) => looksLikeEmail(id));

      const userNameMap: Record<string, string> = {};
      try {
        if (supabase && uuidLike.length > 0) {
          const { data: usersById } = await supabase
            .from('users')
            .select('id, external_id, name, email')
            .in('id', uuidLike);
          const { data: usersByExtId } = await supabase
            .from('users')
            .select('id, external_id, name, email')
            .in('external_id', uuidLike);
          const fromIds = [...(usersById ||
            []), ...(usersByExtId || [])];
          for (const user of fromIds) {
            const displayName = user.name || user.email?.split('@')[0] || 'Unknown';
            userNameMap[user.id] = displayName;
            if (user.external_id) userNameMap[user.external_id] = displayName;
            if (user.email) userNameMap[user.email] = displayName;
          }
        }
        if (supabase && emailLike.length > 0) {
          const { data: usersByEmail } = await supabase
            .from('users')
            .select('id, email, name')
            .in('email', emailLike);
          for (const user of usersByEmail || []) {
            const displayName = user.name || user.email?.split('@')[0] || 'Unknown';
            if (user.email) userNameMap[user.email] = displayName;
            userNameMap[user.id] = displayName;
          }
        }
      } catch (err) {
        console.error('[TriageQueue] Error looking up users:', err);
      }

      if (currentUserId && currentUserName) {
        userNameMap[currentUserId] = currentUserName;
      }

      if (cancelled) return;
      setQueueItems(toQueueRows(sortedAwaiting, userNameMap));
    }

    void resolveQueueRows();

    return () => {
      cancelled = true;
    };
  }, [sortedAwaiting, currentUserId, currentUserName]);

  return { items: queueItems, loading: itemsLoading };
}

type TriageViewMode = 'review' | 'table';

export function TriageQueue() {
  const [triageViewMode, setTriageViewMode] = useState<TriageViewMode>('review');
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  // New split-pane state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [notes, setNotes] = useState('');
  const [pendingOutcome, setPendingOutcome] = useState<TriageOutcome | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Existing state kept for redirect and clarification flows
  const [redirectSelections, setRedirectSelections] = useState<Record<string, string>>({});
  const [showRedirectPanel, setShowRedirectPanel] = useState(false);
  const [showTriageGuide, setShowTriageGuide] = useState(false);

  const TRIAGE_GUIDE_SECTIONS: InfoSection[] = [
    {
      id: 'what',
      label: 'What is triage?',
      description: 'Triage is the intake gate: it decides whether a submitted item enters the active backlog and in what form. Every item submitted (from forms, integrations, or manually) arrives here first. The goal is to route it quickly — not to evaluate its priority.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    },
    {
      id: 'accept',
      label: 'Accept',
      tagline: 'This item is real, relevant, and ready for item work.',
      description: 'Accepting moves the item from the intake queue into the main item list with lifecycle stage "active". The item is now visible for prioritization, categorization, and scoring. Acceptance does not mean it will be worked on — that\'s determined by later prioritization.',
      color: 'bg-[var(--green-dim)] border-[var(--green)]',
    },
    {
      id: 'defer',
      label: 'Defer',
      tagline: 'This item might be valid later — not now.',
      description: 'Deferring holds the item in a pending state. It remains in the system but doesn\'t enter the active backlog. Use this when the timing is wrong, more context is needed, or the idea needs to be revisited after a specific event.',
      examples: ['Item is about a feature planned for next fiscal year', 'Needs input from a stakeholder who is unavailable', 'Waiting for a regulatory decision before scoping'],
      color: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
    },
    {
      id: 'reject',
      label: 'Reject',
      tagline: 'This item is not in scope or is a duplicate.',
      description: 'Rejecting removes the item from the queue. It is archived but not deleted. Use this for items that are clearly out of scope, duplicates of existing items, or submissions that don\'t represent real work. A note is required so the submitter understands why.',
      color: 'bg-[var(--coral-dim)] border-[var(--coral)]',
    },
    {
      id: 'redirect',
      label: 'Redirect',
      tagline: 'This item belongs in a different queue or workspace.',
      description: 'Use Redirect when the item is valid but belongs to a different team or process. This sends it to the correct destination without cluttering the current queue.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    },
    {
      id: 'needs-info',
      label: 'Needs Info',
      tagline: 'I can\'t decide without more detail.',
      description: 'Selecting "Needs Info" opens a clarification thread with the submitter. The item stays in the queue until the required information is provided. Useful when the submission is too vague to accept or reject with confidence.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    },
  ];

  // Permission checks — granular triage sub-permissions fall back to ITEM_CRUD so
  // anyone with the legacy items.triage capability still passes all three checks.
  const canPermission = useUserStore(state => state.can) as (action: PermissionAction) => boolean;
  const canApprove = canPermission('items.triage.approve');
  const canRoute = canPermission('items.triage.route');

  // Get store actions directly (stable references)
  const submitTriageAssessment = useTriageStore(state => state.submitTriageAssessment);
  const bulkTriage = useTriageStore(state => state.bulkTriage);
  const orgUnitsMap = useEnterpriseIdentityStore(state => state.orgUnits);

  // Get assessments for stats calculation (raw data, not computed)
  const assessments = useTriageStore(state => state.assessments);
  const availableQueues = useMemo(() => {
    return Array.from(orgUnitsMap.values())
      .filter(ou => ou.queueSettings?.enabled)
      .map(ou => ({ id: ou.id, label: ou.displayName }));
  }, [orgUnitsMap]);

  // Compute stats from raw data with useMemo to avoid infinite loops
  const stats = useMemo(() => {
    const assessmentList = Object.values(assessments);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    return {
      totalTriaged: assessmentList.length,
      triagedToday: assessmentList.filter(a => a.triagedAt && new Date(a.triagedAt) >= today).length,
      triagedThisWeek: assessmentList.filter(a => a.triagedAt && new Date(a.triagedAt) >= weekAgo).length,
      byOutcome: {
        pursue: assessmentList.filter(a => a.outcome === 'pursue').length,
        reject: assessmentList.filter(a => a.outcome === 'reject').length,
        defer: assessmentList.filter(a => a.outcome === 'defer').length,
        needs_clarification: assessmentList.filter(a => a.outcome === 'needs_clarification').length,
        redirect: assessmentList.filter(a => a.outcome === 'redirect').length,
      },
    };
  }, [assessments]);

  // Get real queue items directly from Supabase
  const { items: queueItems, loading: queueLoading } = useRealQueueItems();

  // Filter items based on search
  const filteredItems = useMemo(() =>
    queueItems.filter(i => !searchQuery || i.title?.toLowerCase().includes(searchQuery.toLowerCase())),
    [queueItems, searchQuery]
  );

  const selectedItem = useMemo(() => queueItems.find(i => i.id === selectedId), [queueItems, selectedId]);

  // Auto-select first item when list loads
  useEffect(() => {
    if (filteredItems.length > 0 && !selectedId) setSelectedId(filteredItems[0].id);
  }, [filteredItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear notes and pending state when selection changes
  useEffect(() => {
    setNotes('');
    setPendingOutcome(null);
    setInlineError(null);
    setShowRedirectPanel(false);
  }, [selectedId]);

  // Outcomes that require rationale/clarification before submitting
  const REQUIRES_INPUT: TriageOutcome[] = ['defer', 'reject', 'needs_clarification'];

  const handleOutcomeClick = (outcome: TriageOutcome) => {
    setInlineError(null);
    if (REQUIRES_INPUT.includes(outcome)) {
      setPendingOutcome(outcome);
      return;
    }
    void handleSingleTriage(selectedItem!.id, outcome);
  };

  const handleSingleTriage = async (itemId: string, outcome: TriageOutcome, rationale?: string) => {
    setInlineError(null);
    let redirectTarget: string | undefined;

    if (outcome === 'redirect') {
      redirectTarget = redirectSelections[itemId];
      if (!redirectTarget) {
        setInlineError('Choose a destination queue before redirecting this item.');
        return;
      }
    }

    try {
      await submitTriageAssessment(itemId, {
        itemId,
        quickChecks: createEmptyQuickChecks(),
        outcome,
        clarificationRequest: outcome === 'needs_clarification' ? rationale : undefined,
        rationale: outcome !== 'needs_clarification' ? rationale : undefined,
        redirectTarget,
      });
      setRedirectSelections(prev => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
      setNotes('');
      setPendingOutcome(null);
      // Auto-advance to next item
      const nextItem = filteredItems.find(i => i.id !== itemId);
      if (nextItem) {
        setSelectedId(nextItem.id);
      } else {
        setSelectedId(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save triage assessment.';
      setInlineError(message);
    }
  };

  const handleBulkTriage = async (outcome: TriageOutcome) => {
    const itemIds = filteredItems.map(i => i.id);
    try {
      await bulkTriage({
        itemIds,
        outcome,
        rationale: `Bulk triaged as ${outcome}`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Bulk triage failed.';
      setInlineError(message);
    }
  };

  // Days-in-queue color helper
  const daysColor = (days: number) =>
    days > 5 ? 'text-[var(--coral)]' : days > 3 ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]';

  const daysBadgeColor = (days: number) =>
    days > 5
      ? 'bg-[var(--coral-dim)] text-[var(--coral)] ring-1 ring-red-200'
      : days > 3
      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] ring-1 ring-amber-200'
      : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] ring-1 ring-emerald-200';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <PageHeader
          icon={<Inbox className="h-4 w-4 text-primary-600" />}
          iconBg="bg-primary-50"
          title="Triage Queue"
          subtitle="Review incoming submissions and route them to the right place"
        />
        <div className="mt-1 flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center gap-0.5 bg-[var(--hover-tint)] p-0.5">
            <button
              type="button"
              onClick={() => setTriageViewMode('review')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  transition-all ${
                triageViewMode === 'review'
                  ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              <LayoutList className="h-3.5 w-3.5" />
              Review
            </button>
            <button
              type="button"
              onClick={() => setTriageViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  transition-all ${
                triageViewMode === 'table'
                  ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              <Table2 className="h-3.5 w-3.5" />
              Table
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowTriageGuide(true)}
            title="How does triage work?"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] bg-[var(--bg-raised)] hover:bg-[var(--slate-dim)] border border-[var(--rule)] transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            How triage works
          </button>
        </div>
      </div>
      <InfoModal
        isOpen={showTriageGuide}
        onClose={() => setShowTriageGuide(false)}
        title="Triage Outcomes"
        subtitle="What each decision does — and when to use it"
        sections={TRIAGE_GUIDE_SECTIONS}
        footerNote="Tip: When in doubt between Accept and Defer, prefer Defer. You can always accept later, but accepting prematurely creates noise in the backlog."
      />

      {/* Table view — renders the full ItemListView in triage mode */}
      {triageViewMode === 'table' && (
        <ItemListView viewMode="triage" />
      )}

      {triageViewMode === 'review' && <div className="flex flex-col h-[calc(100vh-12rem)]">
      {/* Stats bar */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-b border-[var(--rule)] dark:border-[var(--rule)] px-6 py-3 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{filteredItems.length}</span>
          <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">in queue</span>
        </div>
        <div className="h-4 w-px bg-[var(--hover-tint)] dark:bg-[var(--rule)]" />
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{stats.triagedToday}</span>
          <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">triaged today</span>
        </div>
        <div className="h-4 w-px bg-[var(--hover-tint)] dark:bg-[var(--rule)]" />
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--green)]" />
            <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-ter)]">{stats.byOutcome.pursue} pursue</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--mustard)]" />
            <span className="text-sm text-[var(--text-sec)]">{stats.byOutcome.defer} defer</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--coral)]" />
            <span className="text-sm text-[var(--text-sec)]">{stats.byOutcome.reject} reject</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--slate)]" />
            <span className="text-sm text-[var(--text-sec)]">{stats.byOutcome.needs_clarification} clarify</span>
          </span>
        </div>
        <div className="flex-1" />
        <span className="text-xs text-[var(--text-ter)]">{stats.triagedThisWeek} this week</span>
      </div>

      {/* Split pane — side by side from 768px; stacked on a phone, list above the item */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden bg-[var(--surface)] dark:bg-[var(--bg)] border border-[var(--rule)] dark:border-[var(--rule)] border-t-0">

        {/* Left: item list */}
        <div className="w-full md:w-80 max-h-[40vh] md:max-h-none flex-shrink-0 border-b md:border-b-0 md:border-r border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] flex flex-col overflow-hidden">

          {/* Search */}
          <div className="px-3 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="no-dark w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] py-2 pl-8 pr-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:border-[var(--slate)] dark:focus:border-[var(--slate)] focus:bg-[var(--bg-raised)] dark:focus:bg-[var(--surface)] focus:ring-1 focus:ring-indigo-400 dark:focus:ring-[var(--active-tint)] outline-none transition"
              />
            </div>
          </div>

          {/* Item cards */}
          <div className="flex-1 overflow-y-auto">
            {queueLoading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="animate-spin h-6 w-6 border-2 border-[var(--slate)] border-t-transparent rounded-full" />
                <p className="text-xs text-[var(--text-ter)]">Loading queue…</p>
              </div>
            )}

            {!queueLoading && filteredItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-2 px-4 text-center">
                <Inbox className="h-8 w-8 text-[var(--text-ter)]" />
                <p className="text-sm font-medium text-[var(--text-ter)]">Queue is empty</p>
                <p className="text-xs text-[var(--text-ter)]">No items waiting for triage.</p>
              </div>
            )}

            {!queueLoading && filteredItems.map((item) => {
              const isActive = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className={`w-full text-left px-4 py-3 border-b border-[var(--rule)] transition-colors ${
                    isActive
                      ? 'border-l-2 border-l-indigo-500 bg-[var(--slate-dim)]/50'
                      : 'border-l-2 border-l-transparent hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  <p className={`text-sm font-medium line-clamp-2 leading-snug mb-1.5 ${isActive ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                    {item.title}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--text-ter)] truncate max-w-[140px]">
                      {item.submittedByName}
                    </span>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${daysBadgeColor(item.daysInQueue)}`}>
                      {item.daysInQueue}d
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {!selectedItem ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <div className="h-14 w-14 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] flex items-center justify-center">
                <Inbox className="h-7 w-7 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
              </div>
              <p className="text-base font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)]">Select an item to triage</p>
              <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] max-w-xs">
                Choose an item from the list to review and make a triage decision.
              </p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-6">

              {/* Item header */}
              <div>
                <h1 className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug">{selectedItem.title}</h1>
                <div className="mt-2 flex items-center gap-4 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span className={`font-medium ${daysColor(selectedItem.daysInQueue)}`}>
                      {selectedItem.daysInQueue} day{selectedItem.daysInQueue !== 1 ? 's' : ''} in queue
                    </span>
                  </span>
                  <span>by {selectedItem.submittedByName}</span>
                  {selectedItem.source && (
                    <span className="inline-flex items-center rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] px-2 py-0.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-ter)]">
                      {selectedItem.source}
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedItem.description && (
                <div className="bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3">
                  <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">{selectedItem.description}</p>
                </div>
              )}

              {/* Outcome cards — 2×2 grid */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)]">Triage Decision</p>
                  {!canApprove && (
                    <span className="inline-flex items-center gap-1 text-xs text-[var(--mustard)] font-medium">
                      <Lock className="h-3 w-3" />
                      Requires <code className="bg-[var(--mustard-dim)] px-1 rounded">items.triage.approve</code>
                    </span>
                  )}
                </div>
                <div className={`grid grid-cols-2 gap-3 ${!canApprove ? 'opacity-50 pointer-events-none' : ''}`}>

                  {/* Pursue */}
                  <button
                    onClick={() => handleOutcomeClick('pursue')}
                    disabled={!canApprove}
                    className="group flex flex-col gap-2 border-2 border-[var(--green)] bg-[var(--green-dim)] px-4 py-4 text-left transition hover:border-[var(--green)] hover:bg-[var(--green-dim)] focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[var(--green-dim)] group-hover:bg-[var(--green-dim)] flex items-center justify-center transition">
                        <Check className="h-4 w-4 text-[var(--green)]" />
                      </div>
                      <span className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Pursue</span>
                    </div>
                    <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] leading-snug">
                      Worth continued investment — becomes active.
                    </p>
                  </button>

                  {/* Defer */}
                  <button
                    onClick={() => handleOutcomeClick('defer')}
                    disabled={!canApprove}
                    className="group flex flex-col gap-2 border-2 border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-4 text-left transition hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[var(--mustard-dim)] group-hover:bg-[var(--mustard-dim)] flex items-center justify-center transition">
                        <Clock className="h-4 w-4 text-[var(--mustard)]" />
                      </div>
                      <span className="text-sm font-semibold text-[var(--mustard)]">Defer</span>
                    </div>
                    <p className="text-xs text-[var(--mustard)] leading-snug">
                      Not now — keep for future consideration.
                    </p>
                  </button>

                  {/* Reject */}
                  <button
                    onClick={() => handleOutcomeClick('reject')}
                    disabled={!canApprove}
                    className="group flex flex-col gap-2 border-2 border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-4 text-left transition hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] focus:outline-none focus:ring-2 focus:ring-red-400 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[var(--coral-dim)] group-hover:bg-[var(--coral-dim)] flex items-center justify-center transition">
                        <X className="h-4 w-4 text-[var(--coral)]" />
                      </div>
                      <span className="text-sm font-semibold text-[var(--coral)]">Reject</span>
                    </div>
                    <p className="text-xs text-[var(--coral)] leading-snug">
                      Doesn't fit — archive and move on.
                    </p>
                  </button>

                  {/* Needs Info */}
                  <button
                    onClick={() => handleOutcomeClick('needs_clarification')}
                    disabled={!canApprove}
                    className="group flex flex-col gap-2 border-2 border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-4 text-left transition hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-[var(--slate-dim)] group-hover:bg-[var(--slate-dim)] flex items-center justify-center transition">
                        <HelpCircle className="h-4 w-4 text-[var(--text-ter)]" />
                      </div>
                      <span className="text-sm font-semibold text-[var(--text-sec)]">Needs Info</span>
                    </div>
                    <p className="text-xs text-[var(--text-sec)] leading-snug">
                      Can't decide yet — request clarification.
                    </p>
                  </button>
                </div>
              </div>

              {/* Redirect section (collapsed by default) */}
              <div className="border border-[var(--rule)] overflow-hidden">
                <button
                  onClick={() => canRoute && setShowRedirectPanel(v => !v)}
                  disabled={!canRoute}
                  className="w-full flex items-center justify-between px-4 py-3 bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition text-left disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
                    {canRoute ? (
                      <ArrowRight className="h-4 w-4 text-[var(--text-ter)]" />
                    ) : (
                      <Lock className="h-4 w-4 text-[var(--mustard)]" />
                    )}
                    <span className="font-medium">Redirect to another queue</span>
                    {canRoute && availableQueues.length === 0 && (
                      <span className="text-xs text-[var(--text-ter)]">(no queues available)</span>
                    )}
                    {!canRoute && (
                      <span className="text-xs text-[var(--mustard)]">Requires <code className="bg-[var(--mustard-dim)] px-1 rounded">items.triage.route</code></span>
                    )}
                  </div>
                  {showRedirectPanel ? (
                    <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
                  )}
                </button>
                {showRedirectPanel && (
                  <div className="px-4 py-3 bg-[var(--surface)] border-t border-[var(--rule)]">
                    {availableQueues.length === 0 ? (
                      <p className="text-xs text-[var(--text-ter)]">
                        No enabled queues are available for redirect.
                      </p>
                    ) : (
                      <div className="flex items-center gap-3">
                        <select
                          value={redirectSelections[selectedItem.id] ?? ''}
                          onChange={(e) => setRedirectSelections(prev => ({
                            ...prev,
                            [selectedItem.id]: e.target.value,
                          }))}
                          className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-sec)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 outline-none"
                        >
                          <option value="">Choose destination queue…</option>
                          {availableQueues.map((queue) => (
                            <option key={queue.id} value={queue.id}>
                              {queue.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleSingleTriage(selectedItem.id, 'redirect')}
                          disabled={!redirectSelections[selectedItem.id]}
                          className="inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:cursor-not-allowed disabled:opacity-40 transition"
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                          Redirect
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Relationships */}
              <TriageRelationshipsPanel itemId={selectedItem.id} />

              {/* Inline error */}
              {inlineError && (
                <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)]">
                  {inlineError}
                </div>
              )}

              {/* Contextual rationale / clarification panel */}
              {pendingOutcome && (
                <div className={` border-2 px-4 py-4 space-y-3 ${
                  pendingOutcome === 'defer'              ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]' :
                  pendingOutcome === 'reject'             ? 'border-[var(--coral)] bg-[var(--coral-dim)]' :
                  pendingOutcome === 'needs_clarification'? 'border-[var(--slate)] bg-[var(--slate-dim)]' :
                                                           'border-[var(--rule)] bg-[var(--surface)]'
                }`}>
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-semibold ${
                      pendingOutcome === 'defer'               ? 'text-[var(--mustard)]' :
                      pendingOutcome === 'reject'              ? 'text-[var(--coral)]' :
                      pendingOutcome === 'needs_clarification' ? 'text-[var(--text-ter)]' :
                                                                 'text-[var(--text-pri)]'
                    }`}>
                      {pendingOutcome === 'needs_clarification'
                        ? 'What clarification do you need from the submitter?'
                        : `Why are you ${pendingOutcome === 'defer' ? 'deferring' : 'rejecting'} this item?`}
                    </p>
                    <button
                      type="button"
                      onClick={() => { setPendingOutcome(null); setNotes(''); setInlineError(null); }}
                      className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition"
                    >
                      Cancel
                    </button>
                  </div>
                  <textarea
                    autoFocus
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={
                      pendingOutcome === 'needs_clarification'
                        ? 'Describe what information you need…'
                        : pendingOutcome === 'defer'
                        ? 'e.g. Revisit after Q3 planning, waiting on stakeholder input…'
                        : 'e.g. Duplicate of existing item, out of scope for this team…'
                    }
                    className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-sec)] placeholder:text-[var(--text-ter)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 outline-none resize-none transition"
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={!notes.trim()}
                      onClick={() => void handleSingleTriage(selectedItem.id, pendingOutcome, notes.trim())}
                      className={`inline-flex items-center gap-1.5  px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-40 ${
                        pendingOutcome === 'defer'               ? 'bg-[var(--mustard)] hover:bg-[var(--mustard)]' :
                        pendingOutcome === 'reject'              ? 'bg-[var(--coral)] hover:bg-[var(--coral)]' :
                        pendingOutcome === 'needs_clarification' ? 'bg-[var(--slate)] hover:bg-[var(--slate)]' :
                                                                   'bg-[var(--slate)] hover:bg-[var(--slate)]'
                      }`}
                    >
                      Confirm {pendingOutcome === 'defer' ? 'Defer' : pendingOutcome === 'reject' ? 'Reject' : 'Request'}
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
      </div>}
    </div>
  );
}

export default TriageQueue;
